import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams } from "react-router-dom";
import FilesPage from "./FilesGridPage";
import ErrorPage from "../components/ErrorPage";
import secureLocalStorage from "react-secure-storage";
import UnifiedUpload from "../components/filesection/UnifiedUpload";
import SecureShare from "../components/filesection/SecureShare";
import { useDarkMode } from "../context/darkModeContext";
import useAuth from "../stores/authStore";
import { api } from "../utils/axios-instance";
import MultipleFileMenu from "../components/MultipleFileMenu";
import CreateFile from "../components/filesection/CreateFile";
import { useDepartment } from "../stores/departmentStore";
import { useFileStore } from "../store/fileStore";
import DepartmentFoldersSection from "../components/departments/DepartmentFoldersSection";

// Responsive files-per-page: 50 desktop, 30 tablet, 20 mobile
function useFilesPerPage() {
  const getCount = useCallback(() => {
    const w = window.innerWidth;
    if (w >= 1280) return 50;
    if (w >= 768) return 30;
    return 20;
  }, []);

  const [filesPerPage, setFilesPerPage] = useState(getCount);

  useEffect(() => {
    const handler = () => setFilesPerPage(getCount());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [getCount]);

  return filesPerPage;
}

const DepartmentPage = () => {
  const { darkMode } = useDarkMode();
  const formatFileSize = useAuth((state) => state.formatFileSize);
  const profileData = useAuth((state) => state.profileData);
  const { deptName } = useParams();
  const decodedDeptName = decodeURIComponent(deptName || "").trim();
  const [loading, setLoading] = useState(true); // Initialize loading state as true
  const departmentFiles = useFileStore((state) => state.departmentFiles);
  const fileRefreshKey = useFileStore((state) => state.fileRefreshKey);
  const setDepartmentFiles = useFileStore((state) => state.setDepartmentFiles);
  const selectedFiles = useFileStore((state) => state.selectedFiles);
  const departments = useDepartment((state) => state.departments);
  const currentDept = useMemo(
    () => departments.find((dept) => dept.name === decodedDeptName),
    [departments, decodedDeptName]
  );
  const filesPerPage = useFilesPerPage();
  const [select, setSelect] = useState(false);
  const [showMultiFileOptions, setShowMultiFileOptions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Stable refs so fetchData can read latest values without being a dep
  const departmentFilesRef = useRef(departmentFiles);
  const pinnedFilesRef = useRef(profileData?.pinned_files);
  useEffect(() => {
    departmentFilesRef.current = departmentFiles;
  }, [departmentFiles]);
  useEffect(() => {
    pinnedFilesRef.current = profileData?.pinned_files;
  }, [profileData?.pinned_files]);

  // Reset to page 1 when department changes or filesPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [decodedDeptName, filesPerPage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`/file/files`);

        if (response) {
          const existingDepartmentFilesMap = new Map(
            departmentFilesRef.current.map((file) => [file.id, file])
          );

          const mappedFiles = response.data.map((file) => {
            const pinnedSet = new Set(pinnedFilesRef.current || []);

            const matchedDepartment = departments.find(
              (dept) => dept.id === file.department_ids?.[0]
            );

            return {
              id: file.id,
              name: file?.name?.substring(0, 80),

              owner: file.owner_id,
              profilePic: file.profilePic ?? file.profile_pic ?? "",

              department: matchedDepartment?.name || "",

              dept: file.department_ids ?? [],
              color: matchedDepartment?.metadata?.bg,

              // preview/rendering — required by FileViewPage for mimetypes
              // (e.g. video) that render directly from a download URL
              // instead of going through the presigned-URL endpoint.
              // Missing this caused "You do not have access to the file"
              // for every video opened from a department page.
              download_url: file.download_url ?? file.downloadUrl ?? "",
              downloadUrl: file.download_url ?? file.downloadUrl ?? "",

              drive_link: file?.drive_link,
              drive_file_id: file?.drive_file_id,
              folderId: file.folder_id ?? "",

              // security.*
              isLocked:
                existingDepartmentFilesMap.get(file.id)?.isLocked ??
                file.security?.is_locked ??
                false,
              hasPassword: file.security?.has_password ?? false,
              password: file.security?.password_hash ?? null,
              isPinned:
                existingDepartmentFilesMap.get(file.id)?.isPinned ??
                pinnedSet.has(file.id),

              // versioning.*
              versionId: file.versioning?.versionId ?? "",
              currVersion: file.versioning?.curr_version ?? "",
              versions: file.versioning?.versions ?? [],

              //metadata
              size: formatFileSize(file?.metadata?.size),
              mimetype: file.metadata?.mimetype,

              status: "Team",
              security: "Enhanced",

              lastUpdate: new Date(file.metadata.lastModified).toLocaleString(
                "en-IN",
                {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                }
              ),
            };
          });

          // Sort the mappedFiles array based on the lastUpdate property
          mappedFiles.sort((a, b) => {
            return new Date(b.lastUpdate) - new Date(a.lastUpdate);
          });

          const getDepartmentId = departments.find(
            (dept) => dept.name === decodedDeptName
          );

          const filteredFiles = mappedFiles.filter((file) =>
            file.dept.find((id) => id === getDepartmentId.id)
          );

          // Everyone in the department should see all department files
          const visibleDeptFiles = filteredFiles;

          // const usersFiles = filteredFiles.filter((file) => {
          //   try {
          //     const profileData = JSON.parse(
          //       secureLocalStorage.getItem("profileData")
          //     );
          //     if (
          //       profileData.role_priv !== "org_admin" &&
          //       file.owner !== profileData.id
          //     ) {
          //       return null;
          //     }
          //     // Note: pinned files (may be do not show pinned files from all departments)
          //     // if (
          //     //   value === 4 &&
          //     //   file.owner !== profileData.id &&
          //     //   profileData.role_priv !== "org_admin"
          //     // ) {
          //     //   return null;
          //     // }
          //     return file;
          //   } catch (err) {
          //     console.log(err);
          //     return null;
          //   }
          // });

          // filter out files which have a folder id

          // V2: folder-contained files are still members of the
          // department (the backend tags them with both folder_id AND
          // department_ids on upload — see upload_folder in views.py).
          // Previously this filtered them out entirely, which is why a
          // department page could show zero files even though files
          // existed and were correctly scoped to that department: any
          // file uploaded via "Select folder" was silently hidden here.
          // The dedicated folder-browsing page (/filesInsideFolder/...)
          // is what should restrict by folder_id, not this view.
          const nonFolderFiles = visibleDeptFiles;

          // setFilesFromBackend(nonFolderFiles);
          setDepartmentFiles(nonFolderFiles);
        }

        setLoading(false); // Once data is fetched, set loading to false
      } catch (error) {
        console.error("Error fetching files:", error);
        setLoading(false); // Set loading to false in case of error as well
      }
    };

    fetchData();
  }, [
    decodedDeptName,
    departments,
    formatFileSize,
    setDepartmentFiles,
    fileRefreshKey,
  ]);

  // Team wide auto refresh
  //
  // This used to poll every 1s and call setDepartmentFiles() unconditionally
  // with a brand-new array of new objects on every tick — even when nothing
  // had changed. That state update cascades a re-render all the way down
  // through FilesGridPage -> FileView -> FileViewer -> VideoEditor, which
  // made any open video flicker/restart roughly once a second. The dashboard
  // equivalent (DashboardFilesSection) only polls every 10s, which is why
  // the same bug was far less noticeable there.
  //
  // Fix: poll at the same 10s cadence as the dashboard, and only replace
  // state when the fetched data has actually changed.
  const lastFilesSnapshotRef = useRef("");

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const response = await api.get("/file/files");

        if (!response) return;

        const mappedFiles = response.data.map((file) => {
          const matchedDepartment = departments.find(
            (dept) => dept.id === file.department_ids?.[0]
          );

          return {
            id: file.id,
            name: file?.name?.substring(0, 80),
            owner: file.owner_id,
            profilePic: file.profilePic,
            department: matchedDepartment?.name || "",
            dept: file.department_ids ?? [],
            color: matchedDepartment?.metadata?.bg,
            folderId: file.folder_id ?? "",
            size: formatFileSize(file?.metadata?.size),
            mimetype: file.metadata?.mimetype,
            download_url: file.download_url ?? file.downloadUrl ?? "",
            downloadUrl: file.download_url ?? file.downloadUrl ?? "",
          };
        });

        const currentDept = departments.find(
          (dept) => dept.name === decodedDeptName
        );

        if (!currentDept) return;

        const visibleFiles = mappedFiles.filter(
          (file) => file.dept && file.dept.includes(currentDept.id)
        );

        // Skip the state update entirely when nothing has actually
        // changed, so components downstream (e.g. an open video player)
        // don't re-render — and therefore don't reload — for no reason.
        const snapshot = JSON.stringify(visibleFiles);
        if (snapshot === lastFilesSnapshotRef.current) return;
        lastFilesSnapshotRef.current = snapshot;

        // Same reasoning as the initial fetch above: folder-contained
        // files belong to the department too, don't hide them.
        setDepartmentFiles(visibleFiles);
      } catch (err) {
        console.error(err);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [decodedDeptName, departments, formatFileSize, setDepartmentFiles]);

  const removeFiles = () => {
    const idsToRemove = selectedFiles.map((file) => file.id);

    const updatedFiles = departmentFiles.filter(
      (file) => !idsToRemove.includes(file.id)
    );

    setDepartmentFiles(updatedFiles);
  };

  const indexOfLastFile = currentPage * filesPerPage;
  const indexOfFirstFile = indexOfLastFile - filesPerPage;
  const currentFiles = departmentFiles.slice(indexOfFirstFile, indexOfLastFile);
  const totalPages = Math.max(
    1,
    Math.ceil(departmentFiles.length / filesPerPage)
  );

  // Clamp currentPage when files are deleted and totalPages shrinks
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  if (!secureLocalStorage.getItem("token")) {
    return <ErrorPage error="You are not authorised" />;
  }

  return (
    <div
      className={`w-full min-h-screen ${
        darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
      }`}
    >
      <div
        className={`my-4 mt-12 flex flex-row justify-between items-center w-full px-8 ${
          darkMode ? "bg-gray-900 text-gray-200" : "bg-[#F1F1FF] text-gray-800"
        }`}
      >
        <p
          className={`text-2xl font-semibold ${
            darkMode ? "text-white" : "text-black"
          }`}
        >
          {decodedDeptName} Files
        </p>
        <span className="flex gap-2">
          {showMultiFileOptions && (
            <MultipleFileMenu
              removeMultiSelect={() => setSelect(false)}
              location="department"
              removeFiles={removeFiles}
              // deptName={deptName}
              deptName={decodedDeptName}
            />
          )}
          <SecureShare value={0} />
          <UnifiedUpload value={0} />
          <button
            className="py-1 px-4 rounded-md border"
            onClick={() => setSelect(!select)}
            style={{
              backgroundColor: select ? "grey" : "#1c4ed8",
              color: "white",
            }}
          >
            Select Files
          </button>
          <CreateFile />
          {/* <ShareFile /> */}
        </span>
      </div>

      <div className={`px-4 pt-6 ${darkMode ? "bg-gray-800" : "bg-white"}`}>
        <DepartmentFoldersSection
          deptId={currentDept?.id}
          deptName={decodedDeptName}
          deptColor={currentDept?.metadata?.bg}
        />
      </div>

      <div
        className={`px-4 pb-4 pt-4 ${darkMode ? "bg-gray-800" : "bg-white"}`}
      >
        {/* "Files" header mirrors the "Folders" header above so the two
            sections are clearly separate blocks instead of one long list
            with a single ambiguous title. */}
        <h2
          className={`text-xl font-semibold flex items-center gap-2 mb-3 ${
            darkMode ? "text-white" : "text-black"
          }`}
        >
          {currentDept?.metadata?.bg && (
            <span
              className="inline-block h-3 w-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: currentDept.metadata.bg }}
            />
          )}
          Files
        </h2>
        {loading ? (
          <p className={darkMode ? "text-gray-200" : "text-gray-700"}>
            Loading...
          </p> // Display loading indicator while fetching data
        ) : (
          <>
            <FilesPage
              filteredData={currentFiles}
              loading={loading}
              select={select}
              setSelect={setSelect}
              showMultiFileOptions={showMultiFileOptions}
              setShowMultiFileOptions={setShowMultiFileOptions}
              deptName={decodedDeptName}
              onFileDeleted={(fileId) =>
                setDepartmentFiles(
                  departmentFiles.filter((f) => f.id !== fileId)
                )
              }
            />
            {totalPages > 1 && (
              <div className="flex justify-center gap-4 mt-6">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded disabled:opacity-50 ${
                    darkMode
                      ? "bg-gray-700 text-white"
                      : "bg-gray-300 text-black"
                  }`}
                >
                  Prev
                </button>
                <span
                  className={`px-4 py-2 ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  Page {Math.min(currentPage, totalPages)} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage >= totalPages}
                  className={`px-4 py-2 rounded disabled:opacity-50 ${
                    darkMode
                      ? "bg-gray-700 text-white"
                      : "bg-gray-300 text-black"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DepartmentPage;
